import { getMicStream, releaseMic, micErrorKind } from "@/lib/recorder";

const isLive = (s) => !!s && s.getAudioTracks().some((t) => t.readyState === "live" && t.enabled);

export const micPermission = async () => {
  try { return (await navigator.permissions?.query({ name: "microphone" }))?.state || "unknown"; } catch { return "unknown"; }
};

// Live "Mic ready" status for this tab: "" (not checked yet) | "ready" | "lost"
const STATUS_KEY = "lexv_mic_status";
export const getMicStatus = () => sessionStorage.getItem(STATUS_KEY) || "";
export const setMicStatus = (s) => {
  if (s === "lost" && getMicStatus() !== "ready") return;
  sessionStorage.setItem(STATUS_KEY, s);
  window.dispatchEvent(new Event("lexv-mic-status"));
};

export const micDiagnostics = async (state = {}) => {
  let micCount = -1;
  try { micCount = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "audioinput").length; } catch { /* unavailable */ }
  const b = browserName();
  return {
    error: state.blocked ? "blocked" : state.kind || "", permission: await micPermission(), attempts: state.attempts || 0,
    browser: BROWSER_LABEL[b] || b, user_agent: navigator.userAgent.slice(0, 400),
    platform: (navigator.userAgentData?.platform || navigator.platform || "").slice(0, 80),
    secure: window.isSecureContext, media_devices: !!navigator.mediaDevices?.getUserMedia, mic_count: micCount, page: window.location.pathname.slice(0, 200),
  };
};

// Fresh getUserMedia on every call; resolves {ok, stream} or {ok:false, kind, blocked}
export const requestMic = async ({ fresh = true } = {}) => {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) return { ok: false, kind: "unsupported" };
  if (fresh) releaseMic();
  const t0 = performance.now();
  try {
    const stream = await getMicStream();
    if (!isLive(stream)) { releaseMic(); return { ok: false, kind: "nodevice" }; }
    return { ok: true, stream };
  } catch (e) {
    const kind = micErrorKind(e);
    // No prompt shown (instant reject) or permission stored as denied = browser is blocking it
    const blocked = kind === "denied" && ((await micPermission()) === "denied" || performance.now() - t0 < 350);
    return { ok: false, kind, blocked };
  }
};

export const micStillLive = async () => { try { return isLive(await getMicStream()); } catch { return false; } };

export const browserName = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Edg\//.test(ua)) return "edge";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Android/.test(ua)) return "android";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "chrome";
};

export const UNBLOCK_STEPS = {
  chrome: ["Click the icon to the left of the web address (a lock or sliders).", "Open Site settings, or find Microphone in the list.", "Set Microphone to Allow.", "Come back here and tap \"I've enabled it — check again\"."],
  edge: ["Click the lock icon to the left of the web address.", "Choose Permissions for this site.", "Set Microphone to Allow.", "Come back here and tap \"I've enabled it — check again\"."],
  firefox: ["Click the crossed-out microphone icon in the address bar.", "Remove the \"Blocked\" setting (click the ×) or choose Allow.", "If needed: Settings › Privacy & Security › Permissions › Microphone › Settings, and allow lexversant.ca.", "Tap \"I've enabled it — check again\"."],
  safari: ["In the menu bar choose Safari › Settings for This Website (or Settings › Websites › Microphone).", "Set Microphone for this site to Allow.", "Tap \"I've enabled it — check again\"."],
  ios: ["Tap the \"aA\" (or page) button in the address bar, then Website Settings.", "Set Microphone to Allow.", "If it's still blocked: open the iPhone/iPad Settings app › Safari (or your browser) › Microphone › Allow.", "Return here and tap \"I've enabled it — check again\"."],
  android: ["Tap the icon to the left of the web address, then Permissions.", "Set Microphone to Allow.", "If it's still blocked: Android Settings › Apps › your browser › Permissions › Microphone › Allow.", "Return here and tap \"I've enabled it — check again\"."],
};
export const BROWSER_LABEL = { chrome: "Chrome", edge: "Edge", firefox: "Firefox", safari: "Safari (Mac)", ios: "iPhone / iPad", android: "Android" };
